import React from 'react'
import { Container, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { CustomComponentTypes } from '../../types/landingPageV2'
import { BGVariants } from '../../types/heroTextSection'
import PurchaseCard, { PurchaseCardProps } from './PurchaseCard'
import ReviewCard from './ReviewCard'
import useZipjobPackages from '../../hooks/useZipjobPackages'

interface Props {
  cardColor?: BGVariants
  singlePackage?: CustomComponentTypes | null
  review?: boolean
  variant?: string | null
}

const PackagesContainer: React.FC<Props> = ({
  cardColor,
  review = false,
  variant = null,
  singlePackage = null,
}) => {
  const { classes, cx } = useStyles()
  const {
    reviewPackage,
    standardPackage,
    proPackage,
    elitePackage,
    executivePackage,
    executiveExpandedPackage,
  } = useZipjobPackages(variant)

  const cardProps: Record<string, PurchaseCardProps> = {
    standard: standardPackage,
    pro: proPackage,
    elite: elitePackage,
  }

  let highlightedPackage = null
  switch (singlePackage) {
    case CustomComponentTypes.PackageExecutive:
      highlightedPackage = executivePackage
      break
    case CustomComponentTypes.PackageExecutiveExpanded:
      highlightedPackage = executiveExpandedPackage
      break
    case CustomComponentTypes.PackageFastTrack:
      highlightedPackage = proPackage
      break
    case CustomComponentTypes.PackageLaunch:
      highlightedPackage = standardPackage
      break
    case CustomComponentTypes.PackagePremium:
      highlightedPackage = elitePackage
      break
    case CustomComponentTypes.PackageReview:
      highlightedPackage = reviewPackage
      break
  }

  return (
    <Box
      className={cx(classes.cards, {
        [classes.cardsInPost]: variant === 'inPost',
        [classes.cardsSingle]: !!highlightedPackage,
      })}
    >
      {(review || highlightedPackage === reviewPackage) && (
        <Container
          className={cx(classes.container, classes.containerReview, {
            [classes.containerInPost]: variant === 'inPost',
            [classes.containerSingle]: !!highlightedPackage,
          })}
          disableGutters
        >
          <ReviewCard {...reviewPackage} />
        </Container>
      )}
      {!highlightedPackage &&
        Object.entries(cardProps).map(([key, props]) => (
          <Container
            className={cx({
              [classes.container]: true,
              [classes.containerInPost]: variant === 'inPost',
            })}
            disableGutters
            key={key}
          >
            <PurchaseCard {...props} />
          </Container>
        ))}
      {highlightedPackage && highlightedPackage !== reviewPackage && (
        <Container
          className={cx(classes.container, {
            [classes.containerInPost]: variant === 'inPost',
            [classes.containerSingle]: !!highlightedPackage,
            [classes.containerExpanded]:
              highlightedPackage === executiveExpandedPackage,
          })}
          disableGutters
        >
          <PurchaseCard
            cardColor={cardColor}
            pricePackage={singlePackage}
            {...highlightedPackage}
          />
        </Container>
      )}
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  cards: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',

    '& > div': {
      margin: '0 1em',
    },

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',

      '& > div': {
        margin: '1em 0',
      },
    },
  },
  cardsInPost: {
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
    },
  },
  cardsSingle: {
    '& > div': {
      margin: '0',
    },
  },
  container: {
    margin: '0',
    width: '18.25em',

    [theme.breakpoints.down('lg')]: {
      maxWidth: '18.25em',
      width: '32%',
    },
    [theme.breakpoints.down('md')]: {
      margin: '1em 0',
      maxWidth: 'initial',
      width: '100%',
      paddingInline: '6%',
    },
  },
  containerSingle: {
    [theme.breakpoints.down('lg')]: {
      width: '18.25em',
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  containerInPost: {
    maxWidth: '32.5%',

    [theme.breakpoints.down('lg')]: {
      margin: '1em 0',
      maxWidth: 'unset',
      width: '100%',
    },
  },
  containerReview: {
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
    [theme.breakpoints.down('md')]: {
      display: 'unset',
    },
  },
  containerExpanded: {
    maxWidth: '100%',
    width: '31.75em',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
}))

export default PackagesContainer
